
import {
  IonAvatar,
  IonBadge,
  IonButton,
  IonButtons,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  popoverController
} from '@ionic/vue';
import { defineComponent, ref } from 'vue';
import {
  chevronForwardOutline,
  codeWorkingOutline,
  hardwareChipOutline,
  lockClosedOutline,
  openOutline,
  personCircleOutline,
  rocketOutline,
  shieldHalfOutline
} from 'ionicons/icons';
import { useAuthStore } from '@/store/auth';
import { useRouter } from "vue-router";
import { goToOms } from '@hotwax/dxp-components'
import { appInfo, isMaargLogin, isOmsWithMaarg } from '@/util';
import { translate } from '@/i18n';
import UserActionsPopover from '@/components/UserActionsPopover.vue'
import Image from "@/components/Image.vue";
import { Actions, hasPermission, setPermissions } from '@/authorization'

export default defineComponent({
  name: 'Home',
  components: {
    Image,
    IonAvatar,
    IonBadge,
    IonButton,
    IonButtons,
    IonCard,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonPage
  },
  async ionViewDidEnter() {
    // clearing the redirect URL to break the login and redirection flow
    // if the user navigates to the home page while login
    this.authStore.setRedirectUrl('')
    setPermissions(this.authStore.permissions);
    if(this.authStore.isAuthenticated) {
      await this.authStore.getPermissions();
    }
  },
  computed: {
    // A category with no app the user has access to is not worth showing, so it is dropped
    // from the listing. Reading the permissions state also keeps this in sync once they are
    // fetched, as the ability behind hasPermission is not reactive on its own.
    visibleAppCategory(): any {
      // Nothing is blocked for a logged out user, and while permissions are yet to be
      // fetched every category is shown rather than briefly showing none
      if (!this.authStore.isAuthenticated || !this.authStore.permissions?.length) {
        return this.appCategory
      }

      return Object.keys(this.appCategory).reduce((categories: any, category: string) => {
        const apps = this.appCategory[category].filter((app: any) => !this.isAppBlocked(app))
        if (apps.length) {
          categories[category] = apps
        }

        return categories
      }, {})
    }
  },
  methods: {
    login() {
      // hydrate (pinia-plugin-persistedstate) will sync the app state with the
      // localStorage state for avoiding the case when two launchpad tabs are opened
      // and the user logs in through one and tries to login again from the next tab
      // $hydate will resync the state and hence, update the app UI
      this.authStore.$hydrate({ runHooks: false })
      // push to login only if user is not logged in (after state hydration)
      if (!this.authStore.isAuthenticated) {
        this.router.push('/login')
      }
    },
    async logout() {
      this.authStore.$hydrate({ runHooks: false })
      // hydrate and logout only if user is logged in (authenticated)
      if (this.authStore.isAuthenticated) {
        await this.authStore.logout()
      }
    },
    generateAppLink(app: any, appEnvironment = '') {
      let handle = app.handle
      // Below logic handles the redirection of user to legacy or new app version
      /*
      * Combinations when user to redirected to which app
      *
      * Legacy and New -> Legacy
      * Not Legacy and Not New -> Legacy
      * Legacy and Not New -> Legacy
      * Not Legacy and New -> New
      */
      if(Actions[app.appLegacyPermission] && hasPermission(Actions[app.appLegacyPermission]) || (Actions[app.appPermission] && !hasPermission(Actions[app.appPermission]) && app.appLegacyPermission)) {
        handle = app.handle + "-legacy"
      }

      const oms = isMaargLogin(handle, appEnvironment) || this.authStore.isMoquiOnly ? this.authStore.getMaargOms : this.authStore.getOMS;
      window.location.href = this.scheme + handle + appEnvironment + this.domain + (this.authStore.isAuthenticated ? `/login?oms=${oms.startsWith('http') ? isMaargLogin(handle, appEnvironment) || this.authStore.isMoquiOnly ? oms : oms.includes('/api') ? oms : `${oms}/api/` : oms}&token=${this.authStore.token.value}&expirationTime=${this.authStore.token.expiration}${isMaargLogin(handle, appEnvironment) ? '&omsRedirectionUrl=' + this.authStore.getOMS : isOmsWithMaarg(handle, appEnvironment) ? '&omsRedirectionUrl=' + this.authStore.getMaargOms : ''}` : '')
    },
    async openUserActionsPopover(event: any) {
      const userActionsPopover = await popoverController.create({
        component: UserActionsPopover,
        event,
        showBackdrop: false,
      });

      userActionsPopover.present();
    },
    isAppBlocked(app: any) {
      if(!this.authStore.isAuthenticated) {
        return false
      }

      const hasLegacyPermission = app.appLegacyPermission ? hasPermission(Actions[app.appLegacyPermission]) : false;
      const hasNewPermission = app.appPermission ? hasPermission(Actions[app.appPermission]) : false;

      if ((app.appLegacyPermission || app.appPermission) && !hasLegacyPermission && !hasNewPermission) {
        return true
      }

      if(isMaargLogin(app.handle) && !this.authStore.getMaargOms) {
        return true
      }

      return false;
    }
  },
  setup() {
    const authStore = useAuthStore();
    const router = useRouter();

    const appCategory = appInfo.reduce((obj: any, app: any) => {
      if (obj[app.type]) {
        obj[app.type].push(app)
      } else {
        obj[app.type] = [app]
      }

      return obj
    }, {})

    const scheme = ref('https://')
    const domain = ref('.hotwax.io')
    const uatHandle = ref('-uat')
    const devHandle = ref('-dev')

    return {
      Actions,
      authStore,
      appCategory,
      chevronForwardOutline,
      codeWorkingOutline,
      devHandle,
      domain,
      goToOms,
      isMaargLogin,
      isOmsWithMaarg,
      lockClosedOutline,
      hardwareChipOutline,
      hasPermission,
      openOutline,
      personCircleOutline,
      rocketOutline,
      router,
      scheme,
      shieldHalfOutline,
      translate,
      uatHandle
    }
  }
});
